import useAssessment from '../../../../../hooks/data-entry/useAssessment';
import MultiSelectDropdown from '../../components/MultiSelectDropdown/MultiSelectDropdown';
import IconTooltipButton from '../../../../button/IconTooltipButton';
import DialogConfirmCancel from '../../../../dialog/DialogConfirm';
import ReviewersPanel from '../ReviewersPanel/ReviewersPanel';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { selectAllOption } from '../../constants/autocomplete';
import { updateAssessmentsBuilderClass } from '../../../../../store/actions/dataEntryAssessmentBuilderActions';
import { updateFilter } from '../../../../../store/actions/filter.actions';
import './GlobalReviewers/GlobalReviewers.css';


const SelectClasses = ({ error, open, setOpen, checkedStudentsIds, onChangeAlert }) => {
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [isAddingReviewers, setIsAddingReviewers] = useState(false);
  const [previousOpen, setPreviousOpen] = useState(open);
  const [lastClosedSelectedIds, setLastClosedSelectedIds] = useState([]);
  const [shouldReorder, setShouldReorder] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false); // Add this flag

  const list = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder.classesList || []
  );
  const loading = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.classesListLoading || false
  );
  const selectedTemplate = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.template ?? null
  );
  const selectedClassList = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.class?.selectedClasses ?? []
  );
  const checkedIds = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder?.checkedIds ?? []
  );

  const studentsList = useSelector(
    (state) => state?.dataEntryAssessmentBuilder?.assessmentsBuilder.studentsList || []
  );

  const { getClassesForAssessment } = useAssessment();
  const dispatch = useDispatch();

  // Initialize lastClosedSelectedIds when editing existing assessment
  useEffect(() => {
    if (!hasInitialized && selectedClassList?.length > 0) {
      const currentSelectedIds = selectedClassList
        .filter((item) => item.value !== 'all')
        .map((item) => item.id);
      setLastClosedSelectedIds(currentSelectedIds);
      setHasInitialized(true);
    }
  }, [selectedClassList, hasInitialized]);

  // Initial load when template changes
  useEffect(() => {
    if (selectedTemplate) {
      const filter = {
        yearLevel: selectedTemplate?.yearLevels?.map((yearLevel) => yearLevel?._id) || []
      };
      getClassesForAssessment(filter);
    }
  }, [selectedTemplate]);

  // Track open/close state and handle reordering
  useEffect(() => {
    // When dropdown opens after being closed
    if (open && !previousOpen) {
      setShouldReorder(true);
    }

    // When dropdown closes
    if (!open && previousOpen && selectedTemplate) {
      const filter = {
        yearLevel: selectedTemplate?.yearLevels?.map((yearLevel) => yearLevel._id) || []
      };
      getClassesForAssessment(filter);

      // Save current selected IDs when dropdown closes
      const currentSelectedIds = selectedClassList
        .filter((item) => item.value !== 'all')
        .map((item) => item.id);
      setLastClosedSelectedIds(currentSelectedIds);
      setShouldReorder(false); // Reset reorder flag
    }

    setPreviousOpen(open);
  }, [open, selectedTemplate, selectedClassList]);

  // Memoized class list with selected items first when shouldReorder is true
  const classListToRender = useMemo(() => {
    if (!list?.length) return [];

    const mappedList = list.map((item) => ({ 
      id: item?.id, 
      label: item?.code, 
      value: item?.value 
    }));

    // Only reorder if shouldReorder is true AND we have items to reorder with
    if (shouldReorder && lastClosedSelectedIds.length > 0) {
      const selectedClasses = mappedList.filter((item) => 
        lastClosedSelectedIds.includes(item.id)
      );
      const unselectedClasses = mappedList.filter((item) => 
        !lastClosedSelectedIds.includes(item.id)
      );

      return [selectAllOption, ...selectedClasses, ...unselectedClasses];
    }

    // Return normal order
    return [selectAllOption, ...mappedList];
  }, [list, shouldReorder, lastClosedSelectedIds]);

  const getClassesData = (selectedClasses = [], shouldReturnAll = false) => {
    let uniqueClasses = [];
    for (let i = 0; i < selectedClasses.length; i++) {
      const klass = shouldReturnAll
        ? selectedClasses[i]
        : list.find((item) => String(item._id) === String(selectedClasses[i]));

      if (!klass) continue;
      const reviewers =
        klass?.hasOwnProperty('reviewers') && klass?.reviewers !== null ? klass?.reviewers : klass?.teachers;
      uniqueClasses.push({
        id: klass?._id || klass?.id,
        label: klass?.code,
        code: klass?.code,
        value: klass?._id || klass?.id,
        teachers:
          klass?.teachers?.map((reviewer) => ({
            _id: reviewer?._id,
            code: reviewer?.code,
            firstName: reviewer?.firstName,
            lastName: reviewer?.lastName,
            email: reviewer?.email,
            photo: reviewer?.photo
          })) || [],
        reviewers: reviewers?.map((reviewer) => ({
          _id: reviewer?._id,
          code: reviewer?.code,
          firstName: reviewer?.firstName,
          lastName: reviewer?.lastName,
          email: reviewer?.email,
          photo: reviewer?.photo
        }))
      });
    }

    return { uniqueClasses };
  };

  const onChangeClass = (value = []) => {
    let foundClasses = [];
    const classes = value?.class
      ? value?.class?.filter((item, _, array) => array?.indexOf(item) === array?.lastIndexOf(item))
      : [];

    if (Array.isArray(classes) && classes.length > 0) {
      const alreadyAllSelected = selectedClassList?.filter((item) => item.value === 'all').length > 0;
      const includesAll = classes?.includes('all');

      //When we remove a class we need to remove the checked student ids
      if (classes.length < selectedClassList?.length) {
        const newIdsSet = new Set(classes);
        const removedClass = selectedClassList.find((obj) => !newIdsSet.has(obj?.id));
        const matchingStudentIds = studentsList
          .filter((student) => student?.classes?.some((cls) => cls._id === removedClass?.id))
          .map((student) => student._id);
        const newCheckedIds = checkedIds.filter((id) => !matchingStudentIds.includes(id));
        dispatch(checkedStudentsIds(newCheckedIds));
      }

      //Remove !== all
      if (alreadyAllSelected && includesAll) {
        const filteredClasses = classes?.filter((item) => item !== 'all');
        const { uniqueClasses } = getClassesData(filteredClasses);
        dispatch(
          updateAssessmentsBuilderClass({
            selectedClasses: uniqueClasses ?? []
          })
        );

        return;
      }

      //Select all
      if (!alreadyAllSelected && includesAll) {
        const { uniqueClasses } = getClassesData(list, true);

        dispatch(
          updateAssessmentsBuilderClass({
            selectedClasses: [selectAllOption, ...uniqueClasses] || []
          })
        );
        return;
      }

      //Add list specific
      if (!alreadyAllSelected && !includesAll) {
        const { uniqueClasses } = getClassesData(classes);

        if (classes?.length === list?.length) {
          dispatch(
            updateAssessmentsBuilderClass({
              selectedClasses: [selectAllOption, ...uniqueClasses] || []
            })
          );
          return;
        } else {
          dispatch(
            updateAssessmentsBuilderClass({
              selectedClasses: uniqueClasses || []
            })
          );
          return;
        }
      }

      //Remove all
      if (alreadyAllSelected && !includesAll) {
        dispatch(updateAssessmentsBuilderClass({ selectedClasses: foundClasses || [] }));
        return;
      }
    } else {
      dispatch(checkedStudentsIds([]));
      dispatch(updateAssessmentsBuilderClass({ checkedIds: [], selectedClasses: foundClasses || [] }));
    }
    onChangeAlert();
  };

  const handleReviewerModalClose = () => {
    setShowUsersDialog(false);
    setIsAddingReviewers(false);
    dispatch(updateFilter('assessmentReviewers', {}));
  };

  const handleReviewerModalConfirm = () => {
    setShowUsersDialog(false);
    setIsAddingReviewers(true);
  };

  return (
    <>
      <Box style={{ width: '25%' }}>
        <MultiSelectDropdown
          optionList={classListToRender ?? []}
          loading={loading}
          limit={2}
          value={selectedClassList ?? []}
          onUpdateFilter={onChangeClass}
          filterKey='class'
          label='Classes'
          name='class'
          disabled={!selectedTemplate || loading}
          error={error?.class ? true : false}
          helperText={error?.class}
          placeholder='Choose classes'
          open={open}
          setOpen={setOpen}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
        />
      </Box>
      <Box style={{ width: '5%' }}>
        <IconTooltipButton
          title={'Assign Reviewers'}
          disabled={loading || !selectedTemplate || selectedClassList?.length === 0}
          icon={'user-plus'}
          className={'assessment__reviewers-button shadow-sm'}
          onClick={() => {
            setShowUsersDialog(true);
          }}
        />
      </Box>
      <DialogConfirmCancel
        id={`assign-users-to-assessment-modal`}
        open={showUsersDialog}
        onCancel={handleReviewerModalClose}
        onConfirm={handleReviewerModalConfirm}
        loading={loading}
        title={'Assign assessment reviewers'}
        onCloseHeader={true}
        maxWidth='xl'
        scroll='body'
        className='assign-users-to-assessment-modal'
      >
        <ReviewersPanel
          classesList={selectedClassList || []}
          isConfirming={isAddingReviewers}
          setIsConfirming={setIsAddingReviewers}
        />
      </DialogConfirmCancel>
    </>
  );
};

export default SelectClasses;
